import React, { useState } from 'react';
import { APIKeyCreate } from '../../types/apiKeys';

interface CreateAPIKeyModalProps {
  onClose: () => void;
  onSubmit: (data: APIKeyCreate) => void;
}

export const CreateAPIKeyModal: React.FC<CreateAPIKeyModalProps> = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState<APIKeyCreate>({
    name: '',
    key_type: 'read',
    rate_limit: 1000,
    allowed_ips: [],
    allowed_endpoints: [],
    expires_at: null
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await onSubmit(formData);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof APIKeyCreate, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleIPChange = (index: number, value: string) => {
    const newIPs = [...formData.allowed_ips];
    newIPs[index] = value;
    setFormData(prev => ({ ...prev, allowed_ips: newIPs }));
  };

  const addIP = () => {
    setFormData(prev => ({ 
      ...prev, 
      allowed_ips: [...prev.allowed_ips, ''] 
    }));
  };

  const removeIP = (index: number) => {
    setFormData(prev => ({ 
      ...prev, 
      allowed_ips: prev.allowed_ips.filter((_, i) => i !== index) 
    }));
  };

  const handleEndpointChange = (index: number, value: string) => {
    const newEndpoints = [...formData.allowed_endpoints];
    newEndpoints[index] = value;
    setFormData(prev => ({ ...prev, allowed_endpoints: newEndpoints }));
  };

  const addEndpoint = () => {
    setFormData(prev => ({ 
      ...prev, 
      allowed_endpoints: [...prev.allowed_endpoints, ''] 
    }));
  };

  const removeEndpoint = (index: number) => {
    setFormData(prev => ({ 
      ...prev, 
      allowed_endpoints: prev.allowed_endpoints.filter((_, i) => i !== index) 
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Create New API Key</h2>
          <button
            onClick={onClose}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              Name *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter API key name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              Key Type *
            </label>
            <select
              value={formData.key_type}
              onChange={(e) => handleInputChange('key_type', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="read">Read Only</option>
              <option value="write">Read & Write</option>
              <option value="admin">Full Access</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              Rate Limit (requests per hour) *
            </label>
            <input
              type="number"
              required
              min="1"
              max="100000"
              value={formData.rate_limit}
              onChange={(e) => handleInputChange('rate_limit', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              Expiration Date
            </label>
            <input
              type="datetime-local"
              value={formData.expires_at || ''}
              onChange={(e) => handleInputChange('expires_at', e.target.value || null)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Leave empty for no expiration</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              Allowed IP Addresses
            </label>
            <div className="space-y-2">
              {formData.allowed_ips.map((ip, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={ip}
                    onChange={(e) => handleIPChange(index, e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 192.168.1.1"
                  />
                  <button
                    type="button"
                    onClick={() => removeIP(index)}
                    className="px-3 py-2 text-red-600 dark:text-red-400 hover:text-red-700 dark:text-red-300"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addIP}
                className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:text-blue-300 text-sm"
              >
                + Add IP Address
              </button>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Leave empty to allow all IPs</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              Allowed Endpoints
            </label>
            <div className="space-y-2">
              {formData.allowed_endpoints.map((endpoint, index) => (
                <div key={index} className="flex gap-2">
                  <select
                    value={endpoint}
                    onChange={(e) => handleEndpointChange(index, e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select endpoint</option>
                    <option value="/api/v1/search/">Search</option>
                    <option value="/api/v1/account-finder/">Account Finder</option>
                    <option value="/api/v1/osint/">OSINT</option>
                    <option value="/api/v1/user/profile/">User Profile</option>
                    <option value="/api/v1/credits/balance/">Credits Balance</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => removeEndpoint(index)}
                    className="px-3 py-2 text-red-600 dark:text-red-400 hover:text-red-700 dark:text-red-300"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addEndpoint}
                className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:text-blue-300 text-sm"
              >
                + Add Endpoint
              </button>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Leave empty to allow all endpoints</p>
          </div>

          <div className="flex justify-end gap-3 pt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 rounded-md hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create API Key'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
